import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Save, Plus, X, GripVertical, Search,
  ChevronsUp, ChevronsDown, ChevronUp, ChevronDown,
  Filter, CheckSquare, Square, Star,
} from 'lucide-react';
import { useAuth } from '../context/AppContext';
import {
  editionService, articleService, geographyService,
  categoryService, authorService, maquetaService,
} from '../services/api';
import styles from './EditionEditorPage.module.css';
import MaquetaTab from '../components/maqueta/MaquetaTab';
import PlantillasEditorPage from '../components/maqueta/PlantillasEditorPage';
import PlantillasArticuloEditorPage from '../components/maqueta/PlantillasArticuloEditorPage';

// ── Estilos reutilizables (para evitar strings repetidos inline) ──
const selectStyle = {
  padding: '6px 8px',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 12,
  background: '#fff',
};

const arrowBtn = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  color: '#666',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const statusLabel = (s) => ({
  published: 'Publicado',
  draft: 'Borrador',
  in_review: 'En revisión',
  archived: 'Archivado',
}[s] || s || '—');

const statusColor = (s) => ({
  published: '#1a7f37',
  draft: '#888',
  in_review: '#a15c00',
  archived: '#666',
}[s] || '#666');

export default function EditionEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const isNew = !id;
  
  const [formData, setFormData] = useState({
    number: '',
    title: '',
    date: new Date().toISOString().split('T')[0],
    status: 'draft',
    isDefault: false,
    /** Total de páginas físicas de la impresa (múltiplo de 4). */
    totalPages: 16,
    // Geografía. departmentId sirve solo para poblar el combo de
    // municipios; lo que se guarda en la BD es municipalityId (o NULL
    // cuando la edición es nacional).
    departmentId: '',
    municipalityId: '',
    articles: []
  });
  
  const [availableArticles, setAvailableArticles] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableAuthors, setAvailableAuthors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Toast global: cualquier componente descendiente puede disparar
  // window.dispatchEvent(new CustomEvent('app:toast', { detail: 'texto' }))
  // para mostrar un toast verde flotante durante 1.5s.
  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail?.mensaje || e.detail || '';
      if (!msg) return;
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(''), 1500);
    };
    window.addEventListener('app:toast', handler);
    return () => window.removeEventListener('app:toast', handler);
  }, []);
  const [editorTab, setEditorTab] = useState('contenido'); // 'contenido' | 'maqueta' | 'plantillas'
  // Índice articuloId → numeroPagina en la maqueta impresa. Se usa para
  // pintar el badge "En impresa: Pág X" o "Sin ubicar" al lado de cada
  // artículo asignado. Se recarga al cambiar de tab o al guardar.
  const [articulosEnMaqueta, setArticulosEnMaqueta] = useState(new Map());

  // ── Filtros del listado de artículos disponibles ─────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterAuthorId, setFilterAuthorId] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all|published|in_review|draft|archived
  // Set de ids de artículos marcados con checkbox (selección múltiple).
  const [checkedIds, setCheckedIds] = useState(() => new Set());

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadData();
  }, [id, isAuthenticated, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Cargar TODOS los artículos (cualquier estado), catálogos y
      // datos de la edición si existe. Traemos categorías y autores
      // para poblar los filtros. Límite generoso — un periódico rara
      // vez supera 500 artículos por edición.
      const [articlesResponse, deptList, catList, authList] = await Promise.all([
        articleService.getAll({ limit: 500, status: 'all' }),
        geographyService.getAllDepartments().catch(() => []),
        categoryService.getAll().catch(() => []),
        authorService.getAll().catch(() => []),
      ]);
      setAvailableArticles(articlesResponse.data || []);
      setDepartments(deptList || []);
      setAvailableCategories(catList || []);
      setAvailableAuthors(authList || []);

      // Si es edición existente, cargar datos
      if (!isNew) {
        const edition = await editionService.getById(id);
        setFormData({
          number: edition.number || '',
          title: edition.title || '',
          date: edition.publishedAt ? edition.publishedAt.split('T')[0] : new Date().toISOString().split('T')[0],
          status: edition.status || 'draft',
          isDefault: !!edition.isDefault,
          totalPages: edition.totalPages ?? 16,
          departmentId:   edition.departmentId   ?? '',
          municipalityId: edition.municipalityId ?? '',
          articles: edition.articles || []
        });
        // Si venía con departamento, precargamos su lista de municipios
        if (edition.departmentId) {
          const munis = await geographyService.getCities(edition.departmentId).catch(() => []);
          setMunicipalities(munis || []);
        }

        // Índice para badges cruzados: qué artículo está en qué página impresa.
        // Silencioso si falla (ediciones viejas sin maqueta no tienen páginas).
        try {
          const pgs = await maquetaService.listarPaginas(id);
          const m = new Map();
          for (const p of pgs) for (const b of p.bloques) {
            if (b.articuloId) m.set(b.articuloId, p.numeroPagina);
          }
          setArticulosEnMaqueta(m);
        } catch { /* sin maqueta, sin badges */ }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar datos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.number || !formData.title) {
      setError('Número y título son requeridos');
      return;
    }
    
    setSaving(true);

    try {
      const data = {
        number: parseInt(formData.number),
        title: formData.title,
        description: '',
        coverImage: null,
        pdfUrl: null,
        pages: formData.articles.length,
        publishedAt: formData.date,
        date: formData.date,
        status: formData.status,
        isDefault: formData.isDefault,
        totalPages: formData.totalPages,
        municipalityId: formData.municipalityId
          ? parseInt(formData.municipalityId, 10)
          : null,
        articleIds: formData.articles.map(a => a.id || a),
        // IMPORTANTE: preservar isCover/order/page en el objeto que
        // pasamos al service. El mapper de api.js necesita art.isCover
        // para convertirlo en esPortada. Antes se descartaban con
        // .map(a => ({ articleId: a.id })) y por eso la portada nunca
        // se persistía.
        articles: formData.articles.map((a, idx) => ({
          articleId: a.id || a,
          isCover:   !!a.isCover,
          order:     a.order ?? idx,
          page:      a.page ?? null,
        })),
      };

      let response;
      if (isNew) {
        response = await editionService.create(data);
      } else {
        response = await editionService.update(id, data);
      }

      // El backend separa 'editar datos' de 'publicar'. El endpoint
      // create/update NO cambia el estado (siempre queda como está,
      // borrador por defecto). Para publicar hay que llamar al
      // endpoint dedicado POST /ediciones/{id}/publicar.
      // Si el usuario dejó Estado=published en el formulario y el
      // resultado del backend viene aún en borrador, disparamos el
      // publicar contra el id de la edición (nueva o existente).
      if (formData.status === 'published' && response?.status !== 'published') {
        try {
          response = await editionService.publish(response.id || id);
        } catch (pubErr) {
          console.error('No se pudo publicar la edición:', pubErr);
          throw new Error(
            'La edición se guardó pero no se pudo cambiar a Publicado: ' +
            (pubErr.message || 'error desconocido'));
        }
      }

      const msg = response.message || 'Edición guardada correctamente';
      setSuccessMsg(msg);
      setTimeout(() => {
        setSuccessMsg('');
        navigate('/admin');
      }, 1500);
    } catch (err) {
      console.error('Error saving:', err);
      // setError('Error al guardar la edición: ' + err.message);
      const errorMsg = err.response?.error || err.message;
      setError('Error al guardar la edición: ' + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const addArticle = (article) => {
    if (!formData.articles.find(a => (a.id || a) === article.id)) {
      setFormData({
        ...formData,
        articles: [...formData.articles, article]
      });
    }
  };

  const removeArticle = (articleId) => {
    setFormData({
      ...formData,
      articles: formData.articles.filter(a => (a.id || a) !== articleId)
    });
  };

  // ── Selección múltiple + bulk actions ────────────────────────────

  const toggleCheck = (articleId) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(articleId)) next.delete(articleId);
      else next.add(articleId);
      return next;
    });
  };

  const clearCheck = () => setCheckedIds(new Set());

  const checkAllVisible = () => {
    setCheckedIds(new Set(filteredArticles.map(a => a.id)));
  };
  const uncheckAllVisible = () => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      filteredArticles.forEach(a => next.delete(a.id));
      return next;
    });
  };

  // Añadir todos los seleccionados que aún no están en la edición.
  const addChecked = () => {
    const toAdd = availableArticles.filter(a =>
      checkedIds.has(a.id) &&
      !formData.articles.find(x => (x.id || x) === a.id)
    );
    if (toAdd.length === 0) return;
    setFormData(prev => ({ ...prev, articles: [...prev.articles, ...toAdd] }));
    clearCheck();
  };

  // Quitar todos los seleccionados de la edición (para limpieza masiva).
  const removeChecked = () => {
    if (checkedIds.size === 0) return;
    setFormData(prev => ({
      ...prev,
      articles: prev.articles.filter(a => !checkedIds.has(a.id || a)),
    }));
    clearCheck();
  };

  // Cuando cambia el departamento seleccionado: cargar sus municipios y
  // limpiar el municipio actual (probablemente ya no aplica).
  const handleDepartmentChange = async (deptId) => {
    setFormData(prev => ({
      ...prev,
      departmentId:   deptId,
      municipalityId: '',
    }));
    if (!deptId) {
      setMunicipalities([]);
      return;
    }
    try {
      const munis = await geographyService.getCities(parseInt(deptId, 10));
      setMunicipalities(munis || []);
    } catch (_err) {
      setMunicipalities([]);
    }
  };

  // Cuando marca "edición nacional", limpiamos la geografía y
  // deshabilitamos los combos (ambas cosas son mutuamente excluyentes).
  const handleIsDefaultChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      isDefault: checked,
      ...(checked ? { departmentId: '', municipalityId: '' } : {}),
    }));
    if (checked) setMunicipalities([]);
  };

  const moveArticle = (index, direction) => {
    const newArticles = [...formData.articles];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newArticles.length) return;

    [newArticles[index], newArticles[newIndex]] = [newArticles[newIndex], newArticles[index]];
    setFormData({ ...formData, articles: newArticles });
  };

  // Botones "al inicio" / "al final" — útiles con muchos artículos.
  const moveArticleTo = (index, target) => {
    if (index === target) return;
    const arr = [...formData.articles];
    const [it] = arr.splice(index, 1);
    arr.splice(target, 0, it);
    setFormData({ ...formData, articles: arr });
  };

  // Marca un artículo como portada. Como es único por edición,
  // desmarca cualquier otro que estuviera activo. Toggle: si el
  // ya-portada lo vuelves a tocar, lo desmarcas (edición sin
  // portada explícita).
  const setCover = (articleId) => {
    setFormData(prev => {
      const target = prev.articles.find(a => (a.id || a) === articleId);
      const wasCover = !!target?.isCover;
      // Regla: primero desmarcamos TODOS. Luego, si el clickeado no
      // era portada (wasCover=false), lo marcamos. Si lo era, queda
      // sin portada (toggle off).
      const cleared = prev.articles.map(a =>
        a.isCover ? { ...a, isCover: false } : a);
      const next = wasCover
        ? cleared
        : cleared.map(a => (a.id || a) === articleId ? { ...a, isCover: true } : a);
      return { ...prev, articles: next };
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterCategoryId('');
    setFilterAuthorId('');
    setFilterStatus('all');
  };

  // ── Filtro combinado del listado de disponibles ─────────────────
  const filteredArticles = useMemo(() => {
    const inEdition = new Set(formData.articles.map(a => a.id || a));
    const s = searchTerm.trim().toLowerCase();

    return availableArticles.filter(a => {
      if (inEdition.has(a.id)) return false; // ya vinculado, no repetir

      // Búsqueda por texto (título/extracto)
      if (s) {
        const hay = (a.title || '').toLowerCase().includes(s)
                 || (a.excerpt || '').toLowerCase().includes(s);
        if (!hay) return false;
      }

      // Categoría
      if (filterCategoryId && String(a.category?.id) !== String(filterCategoryId)) return false;

      // Autor: mira el autor principal y también authors[] si existe
      if (filterAuthorId) {
        const aid = String(filterAuthorId);
        const okMain = String(a.author?.id) === aid;
        const okList = Array.isArray(a.authors) && a.authors.some(x => String(x.id) === aid);
        if (!okMain && !okList) return false;
      }

      // Estado
      if (filterStatus !== 'all' && a.status !== filterStatus) return false;

      return true;
    });
  }, [availableArticles, formData.articles, searchTerm, filterCategoryId, filterAuthorId, filterStatus]);

  // Cuántos de los "checked" están visibles ahora mismo (para el UI).
  const checkedVisibleCount = filteredArticles.filter(a => checkedIds.has(a.id)).length;
  const allVisibleChecked = filteredArticles.length > 0 && checkedVisibleCount === filteredArticles.length;

  if (!isAuthenticated) return null;

  if (loading) {
    return <div className={styles.loading}>Cargando...</div>;
  }

  return (
    <main className={styles.editorPage}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to="/admin" className={styles.backBtn}>
            <ArrowLeft size={20} />
            Volver
          </Link>
          <h1>{isNew ? 'Nueva Edición' : 'Editar Edición'}</h1>
        </div>
        <div className={styles.headerActions}>
          {editorTab === 'contenido' && (
            <button
              type="button"
              onClick={() => {
                const form = document.getElementById('editionForm');
                if (form) form.requestSubmit();
              }}
              className={styles.saveBtn}
              disabled={saving}
            >
              <Save size={18} />
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          )}
        </div>
      </header>

      {error && <div className={styles.error}>{error}</div>}
      {successMsg && (
        <div style={{
          position:'fixed', top:20, right:20, zIndex:9999,
          background:'#16a34a', color:'#fff', padding:'12px 20px',
          borderRadius:8, fontSize:14, fontWeight:600,
          boxShadow:'0 8px 24px rgba(0,0,0,0.15)',
          display:'flex', alignItems:'center', gap:8,
          animation:'slideInRight 0.25s ease-out',
        }}>
          ✓ {successMsg}
        </div>
      )}

      {/* Tabs: contenido normal vs maquetación impresa (solo para ediciones ya guardadas) */}
      {!isNew && (
        <div style={{
          display:'flex', gap:8, borderBottom:'1px solid #e5e7eb',
          marginBottom:16, padding:'0 4px',
        }}>
          <button
            type="button"
            onClick={() => setEditorTab('contenido')}
            style={{
              padding:'10px 16px', border:'none', background:'transparent',
              fontSize:14, fontWeight:600, cursor:'pointer',
              borderBottom: editorTab === 'contenido' ? '3px solid #1B4332' : '3px solid transparent',
              color: editorTab === 'contenido' ? '#1B4332' : '#666',
            }}
          >Contenido</button>
          <button
            type="button"
            onClick={() => setEditorTab('maqueta')}
            style={{
              padding:'10px 16px', border:'none', background:'transparent',
              fontSize:14, fontWeight:600, cursor:'pointer',
              borderBottom: editorTab === 'maqueta' ? '3px solid #1B4332' : '3px solid transparent',
              color: editorTab === 'maqueta' ? '#1B4332' : '#666',
            }}
          >Maquetación impresa</button>
          <button
            type="button"
            onClick={() => setEditorTab('plantillas')}
            style={{
              padding:'10px 16px', border:'none', background:'transparent',
              fontSize:14, fontWeight:600, cursor:'pointer',
              borderBottom: editorTab === 'plantillas' ? '3px solid #1B4332' : '3px solid transparent',
              color: editorTab === 'plantillas' ? '#1B4332' : '#666',
            }}
          >Plantillas de página</button>
          <button
            type="button"
            onClick={() => setEditorTab('plantillas-articulo')}
            style={{
              padding:'10px 16px', border:'none', background:'transparent',
              fontSize:14, fontWeight:600, cursor:'pointer',
              borderBottom: editorTab === 'plantillas-articulo' ? '3px solid #1B4332' : '3px solid transparent',
              color: editorTab === 'plantillas-articulo' ? '#1B4332' : '#666',
            }}
          >Plantillas de artículo</button>
        </div>
      )}

      {editorTab === 'maqueta' && !isNew ? (
        <MaquetaTab edicionId={id} />
      ) : editorTab === 'plantillas' && !isNew ? (
        <PlantillasEditorPage />
      ) : editorTab === 'plantillas-articulo' && !isNew ? (
        <PlantillasArticuloEditorPage />
      ) : (
      <form id="editionForm" onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.mainColumn}>
          <div className={styles.panel}>
            <h3 style={{ marginTop: 0 }}>Artículos de la edición</h3>
            <p style={{ color: '#666', fontSize: 13, marginTop: -6 }}>
              A la izquierda: todos los artículos disponibles con filtros y selección múltiple.
              A la derecha: los que quedan vinculados a esta edición, en el orden que aparecerán.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              gap: 16,
              alignItems: 'start',
            }}>

              {/* ─── Columna izquierda: disponibles + filtros ─── */}
              <div style={{
                border: '1px solid #e0e0e0', borderRadius: 6,
                background: '#fafafa', overflow: 'hidden',
              }}>
                <div style={{ padding: 12, background: '#f0f0f0', borderBottom: '1px solid #e0e0e0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <Filter size={14} style={{ color: '#666' }} />
                    <strong style={{ fontSize: 13 }}>Disponibles ({filteredArticles.length})</strong>
                    <button
                      type="button"
                      onClick={clearFilters}
                      style={{
                        marginLeft: 'auto',
                        fontSize: 11, padding: '4px 10px',
                        border: '1px solid #ccc', borderRadius: 999,
                        background: '#fff', cursor: 'pointer', color: '#555',
                      }}
                    >Limpiar filtros</button>
                  </div>

                  {/* Buscador por texto */}
                  <div style={{ position: 'relative', marginBottom: 8 }}>
                    <Search size={14} style={{
                      position: 'absolute', left: 10, top: '50%',
                      transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none',
                    }} />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Buscar por título o extracto…"
                      style={{
                        width: '100%', padding: '7px 8px 7px 30px',
                        border: '1px solid #ccc', borderRadius: 4, fontSize: 13,
                      }}
                    />
                  </div>

                  {/* Filtros: categoría / autor / estado */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <select
                      value={filterCategoryId}
                      onChange={(e) => setFilterCategoryId(e.target.value)}
                      style={selectStyle}
                    >
                      <option value="">Todas las categorías</option>
                      {availableCategories.map(c => (
                        <option key={c.id} value={c.id}>{c.name || c.nombre}</option>
                      ))}
                    </select>
                    <select
                      value={filterAuthorId}
                      onChange={(e) => setFilterAuthorId(e.target.value)}
                      style={selectStyle}
                    >
                      <option value="">Todos los autores</option>
                      {availableAuthors.map(a => (
                        <option key={a.id} value={a.id}>{a.name || a.nombre}</option>
                      ))}
                    </select>
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    style={{ ...selectStyle, marginTop: 8, width: '100%' }}
                  >
                    <option value="all">Cualquier estado</option>
                    <option value="published">Solo publicados</option>
                    <option value="in_review">Solo en revisión</option>
                    <option value="draft">Solo borradores</option>
                    <option value="archived">Solo archivados</option>
                  </select>
                </div>

                {/* Barra de acciones bulk */}
                <div style={{
                  padding: '8px 12px',
                  background: '#fff',
                  borderBottom: '1px solid #eee',
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                }}>
                  <button
                    type="button"
                    onClick={allVisibleChecked ? uncheckAllVisible : checkAllVisible}
                    disabled={filteredArticles.length === 0}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '4px 10px', fontSize: 12,
                      border: '1px solid #ccc', borderRadius: 999,
                      background: '#fff', cursor: 'pointer', color: '#333',
                    }}
                  >
                    {allVisibleChecked ? <CheckSquare size={13} /> : <Square size={13} />}
                    {allVisibleChecked ? 'Ninguno' : 'Todos'} ({filteredArticles.length})
                  </button>
                  {checkedIds.size > 0 && (
                    <>
                      <span style={{ fontSize: 12, color: '#666' }}>
                        {checkedIds.size} marcado{checkedIds.size !== 1 ? 's' : ''}
                      </span>
                      <button
                        type="button"
                        onClick={addChecked}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          padding: '4px 12px', fontSize: 12, fontWeight: 600,
                          border: 'none', borderRadius: 999,
                          background: 'var(--color-primary, #1B4332)', color: '#fff',
                          cursor: 'pointer', marginLeft: 'auto',
                        }}
                      >
                        <Plus size={13} /> Agregar
                      </button>
                    </>
                  )}
                </div>

                {/* Lista scrolleable */}
                <div style={{ maxHeight: 480, overflowY: 'auto', background: '#fff' }}>
                  {filteredArticles.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>
                      No hay artículos que coincidan con los filtros.
                    </div>
                  ) : filteredArticles.map(a => {
                    const checked = checkedIds.has(a.id);
                    return (
                      <label
                        key={a.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 12px', borderBottom: '1px solid #f0f0f0',
                          cursor: 'pointer',
                          background: checked ? '#f0f7f4' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCheck(a.id)}
                          style={{ margin: 0, cursor: 'pointer' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 13, fontWeight: 600, color: '#222',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>{a.title || '(sin título)'}</div>
                          <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                            {a.category && (
                              <span style={{
                                padding: '1px 8px', borderRadius: 999,
                                background: a.category.color || '#666', color: '#fff',
                                fontSize: 10, fontWeight: 600,
                              }}>{a.category.name}</span>
                            )}
                            <span style={{ fontSize: 11, color: statusColor(a.status) }}>
                              {statusLabel(a.status)}
                            </span>
                            {a.author?.name && (
                              <span style={{ fontSize: 11, color: '#888' }}>· {a.author.name}</span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); addArticle(a); }}
                          title="Agregar solo este"
                          style={{
                            background: 'transparent', border: '1px solid #ccc',
                            borderRadius: 4, padding: 4, cursor: 'pointer', color: '#555',
                          }}
                        >
                          <Plus size={14} />
                        </button>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* ─── Columna derecha: artículos ya en la edición ─── */}
              <div style={{
                border: '1px solid #e0e0e0', borderRadius: 6,
                background: '#fff', overflow: 'hidden',
              }}>
                <div style={{
                  padding: '10px 12px', background: 'var(--color-primary, #1B4332)',
                  color: '#fff', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                }}>
                  <strong style={{ fontSize: 13 }}>
                    En esta edición ({formData.articles.length})
                  </strong>
                  <span style={{
                    fontSize: 11, opacity: 0.85,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}>
                    · <Star size={11} style={{ fill: '#c9a227', color: '#c9a227' }} />
                    marca la <strong>portada</strong> (uno por edición)
                  </span>
                  {formData.articles.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`¿Quitar los ${formData.articles.length} artículos de la edición?`)) {
                          setFormData({ ...formData, articles: [] });
                        }
                      }}
                      style={{
                        marginLeft: 'auto', fontSize: 11, padding: '4px 10px',
                        border: '1px solid rgba(255,255,255,0.4)', borderRadius: 999,
                        background: 'transparent', color: '#fff', cursor: 'pointer',
                      }}
                    >Vaciar</button>
                  )}
                </div>

                <div style={{ maxHeight: 620, overflowY: 'auto' }}>
                  {formData.articles.length === 0 ? (
                    <div style={{ padding: 30, textAlign: 'center', color: '#888', fontSize: 13 }}>
                      <Plus size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
                      <div>Sin artículos aún.</div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        Marca artículos a la izquierda y toca <strong>Agregar</strong>.
                      </div>
                    </div>
                  ) : formData.articles.map((article, index) => (
                    <div
                      key={article.id || article}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '10px 12px', borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                        fontSize: 11, color: '#666',
                      }}>
                        <button type="button" onClick={() => moveArticleTo(index, 0)}
                          disabled={index === 0} title="Al inicio" style={arrowBtn}>
                          <ChevronsUp size={11} />
                        </button>
                        <button type="button" onClick={() => moveArticle(index, -1)}
                          disabled={index === 0} title="Subir" style={arrowBtn}>
                          <ChevronUp size={11} />
                        </button>
                        <strong style={{ fontSize: 12 }}>{index + 1}</strong>
                        <button type="button" onClick={() => moveArticle(index, 1)}
                          disabled={index === formData.articles.length - 1} title="Bajar" style={arrowBtn}>
                          <ChevronDown size={11} />
                        </button>
                        <button type="button" onClick={() => moveArticleTo(index, formData.articles.length - 1)}
                          disabled={index === formData.articles.length - 1} title="Al final" style={arrowBtn}>
                          <ChevronsDown size={11} />
                        </button>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, fontWeight: 600, color: '#222',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{article.title}</div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                          {article.isCover && (
                            <span style={{
                              padding: '1px 8px', borderRadius: 999,
                              background: '#c9a227', color: '#1a1a1a',
                              fontSize: 10, fontWeight: 700, letterSpacing: '.03em',
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                            }}>
                              <Star size={9} style={{ fill: '#1a1a1a' }} />
                              PORTADA
                            </span>
                          )}
                          {article.category && (
                            <span style={{
                              padding: '1px 8px', borderRadius: 999,
                              background: article.category.color || '#666', color: '#fff',
                              fontSize: 10, fontWeight: 600,
                            }}>{article.category.name}</span>
                          )}
                          {article.status && (
                            <span style={{ fontSize: 10, color: statusColor(article.status) }}>
                              {statusLabel(article.status)}
                            </span>
                          )}
                          {/* Badge cruzado: dónde está este artículo en la maqueta impresa.
                              Verde = ubicado en una página, amarillo = asignado a la edición
                              pero sin ubicar todavía. */}
                          {articulosEnMaqueta.has(article.id || article) ? (
                            <span style={{
                              padding: '1px 8px', borderRadius: 999,
                              background: '#16a34a', color: '#fff',
                              fontSize: 10, fontWeight: 700,
                            }}>
                              En impresa: Pág. {articulosEnMaqueta.get(article.id || article)}
                            </span>
                          ) : (
                            <span style={{
                              padding: '1px 8px', borderRadius: 999,
                              background: '#fde68a', color: '#92400e',
                              fontSize: 10, fontWeight: 700,
                            }}>
                              Sin ubicar en impresa
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Botón portada: estrella dorada si es portada,
                          contorno si no. Toggle exclusivo por edición. */}
                      <button
                        type="button"
                        onClick={() => setCover(article.id || article)}
                        title={article.isCover ? 'Quitar de portada' : 'Marcar como portada'}
                        style={{
                          background: article.isCover ? '#fff5e6' : 'transparent',
                          border: `1px solid ${article.isCover ? '#c9a227' : '#ccc'}`,
                          color: article.isCover ? '#c9a227' : '#888',
                          cursor: 'pointer', padding: 6, borderRadius: 4,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Star size={16} style={{ fill: article.isCover ? '#c9a227' : 'none' }} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeArticle(article.id || article)}
                        title="Quitar"
                        style={{
                          background: 'transparent', border: 'none', color: '#b00020',
                          cursor: 'pointer', padding: 4, borderRadius: 4,
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.sideColumn}>
          <div className={styles.panel}>
            <h3>Información</h3>

            <div className={styles.field}>
              <label>Número de edición</label>
              <input
                type="number"
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                placeholder="Ej: 125"
                required
              />
            </div>

            <div className={styles.field}>
              <label>Título</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ej: Edición Especial Fin de Año"
                required
              />
            </div>

            <div className={styles.field}>
              <label>Fecha</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>

            <div className={styles.field}>
              <label>Estado</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="draft">Borrador</option>
                <option value="published">Publicado</option>
              </select>
            </div>

            {/* Total de páginas de la impresa. Restricción de imprenta:
                debe ser múltiplo de 4 (cada pliego doblado = 4 caras).
                Presets cubren el 95% de casos; "Personalizado" habilita
                input libre validado también a múltiplo de 4. Al cambiar
                el total, el backend agrega o quita páginas al final del
                edicion_paginas (nunca del medio — no destruye trabajo). */}
            <div className={styles.field}>
              <label>Total de páginas (impresa)</label>
              {[12, 16, 20, 24, 32].includes(formData.totalPages) ? (
                <select
                  value={formData.totalPages}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({
                      ...formData,
                      totalPages: v === 'custom' ? formData.totalPages : parseInt(v, 10),
                    });
                  }}
                >
                  <option value={12}>12 · Edición ligera (semanario compacto)</option>
                  <option value={16}>16 · Edición estándar (recomendado)</option>
                  <option value={20}>20 · Edición estándar+</option>
                  <option value={24}>24 · Edición ampliada (negocio/especial)</option>
                  <option value={32}>32 · Edición extensa (dominical)</option>
                  <option value="custom">Personalizado…</option>
                </select>
              ) : (
                <input
                  type="number"
                  min={4} max={256} step={4}
                  value={formData.totalPages}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10) || 0;
                    setFormData({ ...formData, totalPages: n });
                  }}
                  placeholder="Múltiplo de 4 (8, 12, 16, ...)"
                />
              )}
              <div style={{ fontSize: 11, color: formData.totalPages % 4 === 0 ? '#666' : '#c9a227', marginTop: 4 }}>
                {formData.totalPages % 4 === 0
                  ? 'Al guardar se prepararán las páginas vacías de la maqueta impresa.'
                  : '⚠️ Debe ser múltiplo de 4 (restricción de pliegos de imprenta).'}
              </div>
            </div>

            <div className={styles.field}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => handleIsDefaultChange(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                <span>Marcar como edición nacional</span>
              </label>
              <small style={{ color: '#666', fontSize: 12, marginTop: 4, display: 'block' }}>
                La edición nacional se muestra a los lectores cuando no hay
                una edición regional para su departamento. Solo puede haber una.
              </small>
            </div>

            {/* Región de la edición. Se deshabilita si está marcada como nacional. */}
            <div className={styles.field}>
              <label>Departamento</label>
              <select
                value={formData.departmentId}
                onChange={(e) => handleDepartmentChange(e.target.value)}
                disabled={formData.isDefault}
              >
                <option value="">— Sin departamento —</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.nombre || d.name}</option>
                ))}
              </select>
              <small style={{ color: '#666', fontSize: 12, marginTop: 4, display: 'block' }}>
                Deja vacío para que la edición aplique a nivel nacional (junto
                con "edición nacional" marcada).
              </small>
            </div>

            <div className={styles.field}>
              <label>Municipio</label>
              <select
                value={formData.municipalityId}
                onChange={(e) => setFormData({ ...formData, municipalityId: e.target.value })}
                disabled={formData.isDefault || !formData.departmentId}
              >
                <option value="">— Todo el departamento —</option>
                {municipalities.map(m => (
                  <option key={m.id} value={m.id}>{m.nombre || m.name}</option>
                ))}
              </select>
              <small style={{ color: '#666', fontSize: 12, marginTop: 4, display: 'block' }}>
                Si eliges un municipio, el backend resuelve automáticamente
                departamento, país y coordenadas del centro de emisión.
              </small>
            </div>
          </div>

          <div className={styles.panel}>
            <h3>Resumen</h3>
            <div className={styles.summary}>
              <div className={styles.summaryItem}>
                <span>Artículos:</span>
                <strong>{formData.articles.length}</strong>
              </div>
              <div className={styles.summaryItem}>
                <span>Estado:</span>
                <strong>{formData.status === 'published' ? 'Publicado' : 'Borrador'}</strong>
              </div>
            </div>
          </div>
        </div>
      </form>
      )}
    </main>
  );
}
